import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Button} from 'views/components';
import {Translate, translate, SecureFileGrid} from 'views/module';

const EducationView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const [showDocuments, setShowDocuments] = React.useState(false);

  const handleToggleDocuments = React.useCallback(() => {
    setShowDocuments((prev) => !prev);
  }, []);

  const resolvedEducation = React.useMemo(() => {
    const entries = bioQuestions?.education?.entries ? bioQuestions.education?.entries : [];

    return entries.reduce((accumulator, entry) => {
      const id = typeof entry?.id === 'string' ? entry.id.trim() : '';

      if (!id) {
        return accumulator;
      }

      accumulator.push({
        id,
        school: typeof entry?.school === 'string' ? entry.school.trim() : '',
        fieldOfStudy: typeof entry?.fieldOfStudy === 'string' ? entry.fieldOfStudy.trim() : '',
        degree: typeof entry?.degree === 'string' ? entry.degree.trim() : '',
        dateObtained: typeof entry?.dateObtained === 'string' ? new Date(entry.dateObtained.trim()).getFullYear() : '',
        documents: typeof entry?.documents === 'object' ? entry.documents : [],
      });
      return accumulator;
    }, []);
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(resolvedEducation.length > 0);
  }, [resolvedEducation, setIsComplete]);

  if (resolvedEducation.length === 0) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.Education.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      {
        resolvedEducation.map((entry, key) => {
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #0288d1',
                borderRadius: '12px',
                backgroundColor: '#e1f5fe',
                paddingY: 1,
                paddingX: 1.5,
                gap: 0.5,
              }}
            >
              <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                {entry.fieldOfStudy}
                {entry.degree && ` • ${entry.degree}`}
                {entry.dateObtained && ` • ${entry.dateObtained}`}
                {entry.school && ` • ${entry.school}`}
              </Typography>
              <Stack spacing={0.5} sx={{width: '100%'}}>
                {
                  entry.documents.length > 0 &&
                  <Stack spacing={0.5}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleToggleDocuments}
                      sx={{
                        alignSelf: 'flex-start',
                        padding: 0,
                        textTransform: 'none',
                        minHeight: 0,
                        fontSize: '0.8rem',
                        color: '#0288d1',
                        fontWeight: 500,
                      }}
                    >
                      {
                        showDocuments ?
                        translate('caregiver.CaregiverBiography.Education.HideAttachments') :
                        translate('caregiver.CaregiverBiography.Education.ShowAttachments')
                      }
                    </Button>
                    {
                      showDocuments &&
                      <SecureFileGrid files={entry.documents} />
                    }
                  </Stack>
                }
              </Stack>
            </Box>
          );
        })
      }
    </Stack>
  );
};

EducationView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(EducationView);
